import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Server, Layers, Cpu, Globe } from 'lucide-react';

const STEPS = [
  {
    id: 'erp',
    label: 'Your ERP',
    sublabel: 'SAP, Oracle, NetSuite, Custom',
    icon: Server,
    color: '#64748b',
    size: 'small',
    detail: 'SAP, Oracle, NetSuite, Microsoft Dynamics, or any custom ERP — we integrate natively at the business process level, not just the data level. Our middleware connects into your existing workflows, pulling exactly the data we need. Unlike most vendors, we own this integration end-to-end.',
  },
  {
    id: 'middleware',
    label: 'Taxera Middleware',
    sublabel: 'Embedded. Native. Two-way.',
    icon: Layers,
    color: '#14b8a6',
    size: 'large',
    detail: 'Enterprise-grade middleware embedded in our platform. This isn\'t an API you call — it\'s integration that lives inside your process. Two-way, native, continuous. Data flows out, status flows back. Very few vendors own this layer. We do — and it\'s what makes one-vendor compliance possible.',
  },
  {
    id: 'engine',
    label: 'Compliance Engine',
    sublabel: 'Validate. Format. Cross-check.',
    icon: Cpu,
    color: '#0ea5e9',
    size: 'large',
    detail: 'Data is validated against country-specific rules, formatted for the target authority, cross-checked against other compliance submissions, and prepared for filing — all automatically. Errors are caught here, not by the tax authority.',
  },
  {
    id: 'authority',
    label: 'Tax Authority',
    sublabel: 'Direct submission worldwide',
    icon: Globe,
    color: '#8b5cf6',
    size: 'small',
    detail: 'Direct submission to tax authority portals worldwide. Acknowledgments and status updates flow back through the middleware into your ERP. Your team sees the result where they work — not in a separate portal.',
  },
];

const DataFlowDiagram = () => {
  const [activeStep, setActiveStep] = useState(null);

  return (
    <div className="bg-slate-900 rounded-3xl p-8 md:p-12 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(20,163,184,0.08)_0%,transparent_60%)]" />

      <div className="relative z-10">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-2">
            How data flows through Taxera
          </h2>
          <p className="text-sm text-slate-400">Click any step to learn more</p>
        </div>

        {/* Flow diagram container */}
        <div className="relative mb-8">

          {/* SVG connection paths + particles */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 1000 300"
            preserveAspectRatio="xMidYMid meet"
            style={{ zIndex: 1 }}
          >
            <defs>
              {/* Forward flow gradient */}
              <linearGradient id="flowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#64748b" stopOpacity="0.6" />
                <stop offset="25%" stopColor="#14b8a6" stopOpacity="0.8" />
                <stop offset="75%" stopColor="#0ea5e9" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.6" />
              </linearGradient>

              {/* Return flow gradient */}
              <linearGradient id="returnGrad" x1="100%" y1="0%" x2="0%" y2="0%">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#14b8a6" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#64748b" stopOpacity="0.3" />
              </linearGradient>

              {/* Forward particle glow */}
              <radialGradient id="particleGlow">
                <stop offset="0%" stopColor="#14b8a6" stopOpacity="1" />
                <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
              </radialGradient>

              {/* Return particle glow */}
              <radialGradient id="returnParticleGlow">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
              </radialGradient>

              {/* Forward path */}
              <path
                id="forwardPath"
                d="M 100,130 C 200,130 250,110 370,110 C 490,110 510,110 630,110 C 750,110 800,130 900,130"
                fill="none"
              />

              {/* Return path — curves underneath */}
              <path
                id="returnPath"
                d="M 900,150 C 850,210 750,240 630,240 C 510,240 490,240 370,240 C 250,240 150,210 100,150"
                fill="none"
              />
            </defs>

            {/* Forward flow line */}
            <use href="#forwardPath" stroke="url(#flowGrad)" strokeWidth="2" strokeDasharray="6 4" opacity="0.5" />

            {/* Return flow line */}
            <use href="#returnPath" stroke="url(#returnGrad)" strokeWidth="1.5" strokeDasharray="4 6" opacity="0.4" />

            {/* Forward data packets — 3 particles at different offsets */}
            {[0, 33, 66].map((offset) => (
              <g key={`fwd-${offset}`}>
                <circle r="8" fill="url(#particleGlow)" opacity="0.6">
                  <animateMotion
                    dur="8s"
                    repeatCount="indefinite"
                    begin={`${offset * 0.08}s`}
                  >
                    <mpath href="#forwardPath" />
                  </animateMotion>
                </circle>
                <circle r="3" fill="#14b8a6" opacity="0.9">
                  <animateMotion
                    dur="8s"
                    repeatCount="indefinite"
                    begin={`${offset * 0.08}s`}
                  >
                    <mpath href="#forwardPath" />
                  </animateMotion>
                </circle>
              </g>
            ))}

            {/* Return data packets — 2 smaller, sparser particles */}
            {[0, 50].map((offset) => (
              <g key={`ret-${offset}`}>
                <circle r="5" fill="url(#returnParticleGlow)" opacity="0.4">
                  <animateMotion
                    dur="12s"
                    repeatCount="indefinite"
                    begin={`${offset * 0.12}s`}
                  >
                    <mpath href="#returnPath" />
                  </animateMotion>
                </circle>
                <circle r="2" fill="#8b5cf6" opacity="0.7">
                  <animateMotion
                    dur="12s"
                    repeatCount="indefinite"
                    begin={`${offset * 0.12}s`}
                  >
                    <mpath href="#returnPath" />
                  </animateMotion>
                </circle>
              </g>
            ))}

            {/* Return path label */}
            <text
              x="500"
              y="260"
              textAnchor="middle"
              className="fill-slate-500 text-xs"
              style={{ fontSize: '11px' }}
            >
              Acknowledgments &amp; status updates flow back natively
            </text>
          </svg>

          {/* Step cards — staggered layout */}
          <div
            className="relative grid grid-cols-2 md:grid-cols-4 items-end gap-3"
            style={{ zIndex: 2, minHeight: '200px' }}
          >
            {STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = activeStep === step.id;
              const isLarge = step.size === 'large';

              return (
                <motion.button
                  key={step.id}
                  onClick={() => setActiveStep(isActive ? null : step.id)}
                  className={`relative rounded-2xl text-center transition-all duration-300 backdrop-blur-sm ${
                    isActive
                      ? 'bg-white/12 ring-1 ring-white/25 shadow-lg shadow-white/5'
                      : 'bg-white/5 hover:bg-white/10'
                  } ${isLarge ? 'p-6 self-stretch' : 'p-5 self-end'}`}
                  whileHover={{ y: -2 }}
                  data-testid={`flow-step-${step.id}`}
                >
                  {/* Glow effect for large (Taxera) cards */}
                  {isLarge && (
                    <div
                      className="absolute inset-0 rounded-2xl opacity-20 blur-xl pointer-events-none"
                      style={{
                        background: `radial-gradient(circle at 50% 80%, ${step.color}40 0%, transparent 70%)`,
                      }}
                    />
                  )}

                  <div className="relative">
                    <div
                      className={`${isLarge ? 'w-12 h-12' : 'w-10 h-10'} rounded-xl mx-auto mb-3 flex items-center justify-center transition-all`}
                      style={{ backgroundColor: `${step.color}20` }}
                    >
                      <Icon
                        className={`${isLarge ? 'w-6 h-6' : 'w-5 h-5'}`}
                        style={{ color: step.color }}
                        strokeWidth={1.5}
                      />
                    </div>
                    <div className={`font-heading font-bold text-white ${isLarge ? 'text-base' : 'text-sm'}`}>
                      {step.label}
                    </div>
                    <div className={`text-slate-500 mt-1 ${isLarge ? 'text-xs' : 'text-2xs'}`}>
                      {step.sublabel}
                    </div>

                    {/* "Where the magic happens" indicator for large cards */}
                    {isLarge && (
                      <div className="mt-3 flex justify-center">
                        <div
                          className="w-1.5 h-1.5 rounded-full animate-pulse"
                          style={{ backgroundColor: step.color }}
                        />
                      </div>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Step detail panel */}
        <AnimatePresence>
          {activeStep && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="bg-white/5 rounded-xl p-6" data-testid="flow-detail">
                {(() => {
                  const s = STEPS.find((step) => step.id === activeStep);
                  return s ? (
                    <div>
                      <h3 className="font-heading text-lg font-bold text-white mb-2">{s.label}</h3>
                      <p className="text-sm text-slate-400">{s.detail}</p>
                    </div>
                  ) : null;
                })()}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DataFlowDiagram;
